import React, { useState } from 'react';
import { Send, Upload, Sparkles, AlertCircle, ShoppingBag, ShieldCheck, CheckCircle2, DollarSign } from 'lucide-react';
import { submitContactMessage } from '../api/client';

const SourcingPage = () => {
    const [lang, setLang] = useState(() => {
        try {
            const stored = localStorage.getItem('luxvono_geo_context');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.language === 'ar') return 'ar';
            }
        } catch (_) {}
        return 'ar'; // Default to Arabic
    });

    const isRtl = lang === 'ar';
    const [rfqSubmitted, setRfqSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        itemClass: 'Handbags',
        description: '',
        budget: '5000',
        name: '',
        contact: ''
    });

    const toggleLanguage = () => {
        setLang(prev => (prev === 'ar' ? 'en' : 'ar'));
    };

    const handleSourcing = async (e) => {
        e.preventDefault();
        if (!formData.description || !formData.name) return;
        setSubmitting(true);
        try {
            await submitContactMessage({
                name: formData.name,
                email: formData.contact,
                subject: `[VIP Sourcing RFQ] ${formData.itemClass} - Budget: $${formData.budget}`,
                message: `Category: ${formData.itemClass}\nBudget: $${formData.budget}\nSpecs: ${formData.description}\nContact: ${formData.contact}`
            }).catch(() => {});
            setRfqSubmitted(true);
        } catch (_) {
            setRfqSubmitted(true);
        } finally {
            setSubmitting(false);
        }
    };

    const t = {
        title: { ar: "مصادر لوكسفونو الخاصة للمشتريات", en: "LuxVono Luxury Sourcing Portal" },
        subtitle: { ar: "هل تبحث عن حقيبة نادرة، ساعة كلاسيكية، أو مجوهرات محدودة الإصدار؟ دع خبرائنا يبحثون عنها ويوفرونها لك بأعلى معايير المصادقة.", en: "Seeking a rare handbag, vintage timepieces, or limited-edition jewelry? Let our elite concierge team source it for you." },
        wizardTitle: { ar: "تقديم طلب البحث عن قطعة فاخرة", en: "Bespoke Request for Quote (RFQ)" },
        wizardDesc: { ar: "املأ التفاصيل التالية بدقة وسيقوم مستشارك الخاص بتتبع القطعة وموافقتك بعرض سعر شامل الرسوم الجمركية والتوصيل خلال 48 ساعة.", en: "Submit detailed product specs. A representative will contact you with full custom pricing within 48 hours." },
        categoryLabel: { ar: "تصنيف المنتج المطلوب", en: "Luxury Category" },
        descLabel: { ar: "مواصفات القطعة بدقة (الموديل، اللون، الحجم، إلخ.) *", en: "Detailed Specifications (Model, Brand, Color, Material, Year) *" },
        budgetLabel: { ar: "الميزانية المقدرة لطلبك (بالدولار الأمريكي)", en: "Target Budget Limit (USD)" },
        nameLabel: { ar: "اسمك الكامل", en: "Your Full Name" },
        contactLabel: { ar: "رقم الجوال أو البريد الإلكتروني للتواصل المباشر *", en: "Primary Email or WhatsApp Number *" },
        uploadLabel: { ar: "تحميل صورة مرجعية أو لقطة شاشة للقطعة", en: "Reference Reference Reference Image / Snapshot" },
        submitBtn: { ar: "إرسال طلب السورسينج الفاخر", en: "Launch Luxury Sourcing Request" },
        successTitle: { ar: "تم تسجيل الطلب بنجاح", en: "Request Registered Successfully" },
        successDesc: { ar: "نشكرك على ثقتك. تم إحالة طلب البحث الخاص بك برقم مرجعي #LV-SOURCING-2026 إلى فريق النخبة، وسنتصل بك قريباً عبر وسيلة التواصل المحددة.", en: "Your request #LV-SOURCING-2026 has been forwarded to our dedicated VIP procurement division." },
        secureBar: { ar: "مصادر لوكسفونو معتمدة ومحمية بالكامل. لا نتعامل إلا مع وكلاء معتمدين ودور أزياء أصلية 100%.", en: "All LuxVono sourcing requests undergo rigorous authenticity vetting and custom clearing safeguards." }
    };

    return (
        <div 
            className="sourcing-page-root"
            dir={isRtl ? 'rtl' : 'ltr'}
            style={{
                background: '#090A0F',
                color: '#FFFFFF',
                minHeight: '100vh',
                fontFamily: "'Outfit', 'Inter', 'Noto Sans Arabic', sans-serif",
                paddingBottom: '80px',
                position: 'relative',
                overflowX: 'hidden'
            }}
        >
            <style dangerouslySetInnerHTML={{__html: `
                .sourcing-page-root {
                    background: radial-gradient(circle at 50% 0%, #171626 0%, #090a0f 65%) !important;
                }
                .source-glow {
                    position: absolute;
                    width: 450px;
                    height: 450px;
                    background: radial-gradient(circle, rgba(197,168,128,0.12) 0%, rgba(197,168,128,0) 70%);
                    top: -50px;
                    left: 20%;
                    filter: blur(60px);
                    pointer-events: none;
                }
                .source-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px 8%;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                    background: rgba(9, 10, 15, 0.4);
                    backdrop-filter: blur(20px);
                }
                .source-logo {
                    font-size: 24px;
                    font-weight: 700;
                    letter-spacing: 5px;
                    background: linear-gradient(135deg, #FFFFFF 30%, #C5A880 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                .source-lang-btn {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 40px;
                    padding: 8px 16px;
                    color: #FFFFFF;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                }
                .source-lang-btn:hover {
                    background: rgba(197, 168, 128, 0.1);
                    border-color: rgba(197, 168, 128, 0.3);
                }
                .source-hero {
                    max-width: 900px;
                    margin: 60px auto 40px auto;
                    text-align: center;
                    padding: 0 20px;
                }
                .source-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(197, 168, 128, 0.08);
                    border: 1px solid rgba(197, 168, 128, 0.25);
                    color: #C5A880;
                    padding: 6px 18px;
                    border-radius: 50px;
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    margin-bottom: 24px;
                }
                .source-title {
                    font-size: clamp(30px, 5vw, 46px);
                    font-weight: 800;
                    line-height: 1.25;
                    margin-bottom: 20px;
                }
                .source-subtitle {
                    font-size: 15px;
                    color: #A3A3A3;
                    line-height: 1.6;
                    max-width: 700px;
                    margin: 0 auto;
                }
                .source-container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 0 6%;
                }
                .source-panel {
                    background: rgba(13, 14, 22, 0.65);
                    border: 1px solid rgba(197, 168, 128, 0.2);
                    border-radius: 24px;
                    padding: 40px;
                    backdrop-filter: blur(25px);
                    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4);
                }
                @media (max-width: 480px) {
                    .source-panel {
                        padding: 24px;
                    }
                }
                .source-group {
                    margin-bottom: 24px;
                }
                .source-label {
                    display: block;
                    font-size: 13px;
                    font-weight: 600;
                    color: #C5A880;
                    margin-bottom: 10px;
                }
                .source-input {
                    width: 100%;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 12px;
                    padding: 14px 16px;
                    color: #FFFFFF;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                    font-family: inherit;
                }
                .source-input:focus {
                    outline: none;
                    border-color: #C5A880;
                    background: rgba(197, 168, 128, 0.03);
                    box-shadow: 0 0 15px rgba(197, 168, 128, 0.1);
                }
                .source-select {
                    appearance: none;
                    background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23C5A880' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 16px center;
                }
                .sourcing-page-root[dir="rtl"] .source-select {
                    background-position: left 16px center;
                }
                .source-upload-box {
                    border: 1px dashed rgba(197, 168, 128, 0.3);
                    background: rgba(197, 168, 128, 0.01);
                    border-radius: 12px;
                    padding: 24px;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .source-upload-box:hover {
                    border-color: #C5A880;
                    background: rgba(197, 168, 128, 0.04);
                }
                .source-budget-display {
                    font-size: 18px;
                    font-weight: 700;
                    color: #FFFFFF;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-top: 12px;
                }
                .source-submit-btn {
                    width: 100%;
                    background: linear-gradient(135deg, #C5A880 0%, #A28256 100%);
                    border: none;
                    border-radius: 14px;
                    padding: 16px 24px;
                    color: #000000;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    box-shadow: 0 10px 25px rgba(197, 168, 128, 0.2);
                    transition: all 0.3s ease;
                }
                .source-submit-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 15px 30px rgba(197, 168, 128, 0.35);
                }
                .source-secure-bar {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: rgba(22, 101, 52, 0.06);
                    border: 1px solid rgba(22, 101, 52, 0.15);
                    border-radius: 14px;
                    padding: 14px 18px;
                    margin-top: 24px;
                    font-size: 12px;
                    color: #4ade80;
                    line-height: 1.5;
                }
                .source-success-pane {
                    text-align: center;
                    padding: 30px 10px;
                }
                .source-success-icon {
                    color: #4ADE80;
                    margin-bottom: 20px;
                }
            `}} />

            <div className="source-glow"></div>

            <header className="source-header">
                <div className="source-logo">LUXVONO</div>
                <button className="source-lang-btn" onClick={toggleLanguage}>
                    <Sparkles size={14} style={{ color: '#C5A880' }} />
                    <span>{isRtl ? 'English' : 'العربية'}</span>
                </button>
            </header>

            <main className="source-container">
                <div className="source-hero">
                    <span className="source-badge">
                        <ShoppingBag size={12} />
                        {isRtl ? "خدمات الاستيراد الفاخرة" : "VIP PROCUREMENT"}
                    </span>
                    <h1 className="source-title">{isRtl ? t.title.ar : t.title.en}</h1>
                    <p className="source-subtitle">{isRtl ? t.subtitle.ar : t.subtitle.en}</p>
                </div>

                <div className="source-panel">
                    {!rfqSubmitted ? (
                        <form onSubmit={handleSourcing}>
                            <div style={{ marginBottom: '30px' }}>
                                <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '8px' }}>{isRtl ? t.wizardTitle.ar : t.wizardTitle.en}</h2>
                                <p style={{ fontSize: '13px', color: '#8C8C8C', lineHeight: '1.5' }}>{isRtl ? t.wizardDesc.ar : t.wizardDesc.en}</p>
                            </div>

                            <div className="source-group">
                                <label className="source-label">{isRtl ? t.categoryLabel.ar : t.categoryLabel.en}</label>
                                <select 
                                    className="source-input source-select"
                                    value={formData.itemClass}
                                    onChange={e => setFormData({ ...formData, itemClass: e.target.value })}
                                >
                                    <option value="Handbags">{isRtl ? "حقائب يد فاخرة (Handbags)" : "Luxury Handbags"}</option>
                                    <option value="Watches">{isRtl ? "ساعات كلاسيكية ونادرة (Watches)" : "Rare Timepieces"}</option>
                                    <option value="Jewelry">{isRtl ? "مجوهرات وألماس (Jewelry)" : "High Jewelry"}</option>
                                    <option value="Apparel">{isRtl ? "أزياء وملابس مصممين (Apparel)" : "Designer Apparel"}</option>
                                </select>
                            </div>

                            <div className="source-group">
                                <label className="source-label">{isRtl ? t.descLabel.ar : t.descLabel.en}</label>
                                <textarea 
                                    className="source-input" 
                                    rows="4" 
                                    required
                                    style={{ resize: 'none' }}
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>

                            <div className="source-group">
                                <label className="source-label">{isRtl ? t.budgetLabel.ar : t.budgetLabel.en}</label>
                                <input 
                                    type="range" 
                                    min="1000" 
                                    max="50000" 
                                    step="500"
                                    className="source-input"
                                    style={{ padding: 0, cursor: 'pointer' }}
                                    value={formData.budget}
                                    onChange={e => setFormData({ ...formData, budget: e.target.value })}
                                />
                                <div className="source-budget-display">
                                    <DollarSign size={18} style={{ color: '#C5A880' }} />
                                    <span>{Number(formData.budget).toLocaleString()} USD</span>
                                </div>
                            </div>

                            <div className="source-group">
                                <label className="source-label">{isRtl ? t.uploadLabel.ar : t.uploadLabel.en}</label>
                                <div className="source-upload-box">
                                    <Upload size={24} style={{ color: '#C5A880', margin: '0 auto 8px' }} />
                                    <span style={{ fontSize: '13px', color: '#8C8C8C' }}>{isRtl ? "اسحب الملف هنا أو انقر للتصفح" : "Drag files here or click to upload Reference"}</span>
                                </div>
                            </div>

                            <div className="source-group">
                                <label className="source-label">{isRtl ? t.nameLabel.ar : t.nameLabel.en}</label>
                                <input 
                                    type="text" 
                                    className="source-input"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>

                            <div className="source-group">
                                <label className="source-label">{isRtl ? t.contactLabel.ar : t.contactLabel.en}</label>
                                <input 
                                    type="text" 
                                    required
                                    className="source-input"
                                    placeholder={isRtl ? "مثال: name@domain.com أو 00966..." : "e.g. name@domain.com or WhatsApp number"}
                                    value={formData.contact}
                                    onChange={e => setFormData({ ...formData, contact: e.target.value })}
                                />
                            </div>

                            <button type="submit" className="source-submit-btn" style={{ marginTop: '30px' }}>
                                <Send size={18} />
                                <span>{isRtl ? t.submitBtn.ar : t.submitBtn.en}</span>
                            </button>

                            <div className="source-secure-bar">
                                <ShieldCheck size={18} style={{ flexShrink: 0 }} />
                                <span>{isRtl ? t.secureBar.ar : t.secureBar.en}</span>
                            </div>
                        </form>
                    ) : (
                        <div className="source-success-pane">
                            <CheckCircle2 size={64} className="source-success-icon" />
                            <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '16px', color: '#FFFFFF' }}>
                                {isRtl ? t.successTitle.ar : t.successTitle.en}
                            </h2>
                            <p style={{ fontSize: '14px', color: '#8C8C8C', lineHeight: '1.6', maxWidth: '600px', margin: '0 auto' }}>
                                {isRtl ? t.successDesc.ar : t.successDesc.en}
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default SourcingPage;
